import { NavLink } from 'react-router';
import {
  BookOpen,
  Boxes,
  Banknote,
  LayoutDashboard,
  Library,
  Package,
  Receipt,
  Scale,
  Settings,
  ShoppingBag,
  UserCog,
  Users,
  Wallet,
  type LucideIcon,
} from 'lucide-react';
import { cn } from '@/lib/cn';

interface NavItem {
  to: string;
  label: string;
  icon: LucideIcon;
  /** When true, displays a small "à venir" pill next to the label. */
  stub?: boolean;
}

interface NavGroup {
  label: string;
  items: ReadonlyArray<NavItem>;
}

/**
 * Sidebar navigation, grouped by domain (validated 2026-05-03).
 * 14 routes : 8 fonctionnels + 6 stubs visibles avec leur badge.
 */
/**
 * Sidebar items whose `to` is a strict prefix of another item's `to`.
 * Without `end` on these, React Router v7's `NavLink` matches them as
 * active for any sub-route — e.g. landing on `/sales/new` lights up
 * BOTH "Nouvelle vente" AND "Historique" (`to="/sales"`). The set is
 * precomputed from the data below so adding a new pair never reintroduces
 * the bug — the audit happens at module load, not at code-review time.
 *
 * `/` is always in the set because it's a prefix of every other path
 * (and the prefix check below uses `to + '/'` which doesn't catch the
 * special case of a bare slash).
 */
function computeExactMatchPaths(nav: ReadonlyArray<NavGroup>): ReadonlySet<string> {
  const allTos = nav.flatMap((g) => g.items.map((i) => i.to));
  const exact = new Set<string>(['/']);
  for (const to of allTos) {
    if (to === '/') continue;
    if (allTos.some((other) => other !== to && other.startsWith(to + '/'))) {
      exact.add(to);
    }
  }
  return exact;
}

const NAV: ReadonlyArray<NavGroup> = [
  {
    label: 'Tableau de bord',
    items: [{ to: '/', label: 'Vue d’ensemble', icon: LayoutDashboard }],
  },
  {
    label: 'Vente',
    items: [
      { to: '/sales/new', label: 'Nouvelle vente', icon: ShoppingBag },
      { to: '/sales', label: 'Historique', icon: Receipt },
    ],
  },
  {
    label: 'Catalogue',
    items: [
      { to: '/products', label: 'Articles', icon: Package },
      { to: '/customers', label: 'Clients', icon: Users },
    ],
  },
  {
    label: 'Comptabilité',
    items: [
      { to: '/accounting/journal', label: 'Journal', icon: BookOpen },
      { to: '/accounting/ledger', label: 'Grand livre', icon: Library },
      { to: '/accounting/balance', label: 'Balance', icon: Scale },
    ],
  },
  {
    label: 'Trésorerie',
    items: [
      { to: '/treasury', label: 'Soldes', icon: Wallet },
      { to: '/cash-register', label: 'Caisses', icon: Banknote, stub: true },
    ],
  },
  {
    label: 'Stock',
    items: [{ to: '/inventory', label: 'Inventaire', icon: Boxes, stub: true }],
  },
  {
    label: 'Équipe',
    items: [{ to: '/users', label: 'Utilisateurs', icon: UserCog }],
  },
  {
    label: 'Paramètres',
    items: [{ to: '/settings', label: 'Entreprise', icon: Settings }],
  },
];

const EXACT_MATCH_PATHS = computeExactMatchPaths(NAV);

export function Sidebar() {
  return (
    <aside className="flex w-60 shrink-0 flex-col border-r border-border-subtle bg-surface-card">
      <div className="flex h-16 items-center px-6">
        <span className="font-display text-2xl font-bold tracking-tight text-brand-primary">
          IOA
        </span>
      </div>
      <nav className="flex-1 overflow-y-auto px-3 pb-6">
        {NAV.map((group) => (
          <div key={group.label} className="mb-5">
            <h3 className="mb-1.5 px-3 text-[10px] font-semibold uppercase tracking-widest text-ink-500">
              {group.label}
            </h3>
            <ul className="space-y-0.5">
              {group.items.map((item) => (
                <li key={item.to}>
                  <NavLink
                    to={item.to}
                    end={EXACT_MATCH_PATHS.has(item.to)}
                    className={({ isActive }) =>
                      cn(
                        'flex items-center gap-2.5 rounded-sm px-3 py-2 text-sm transition',
                        isActive
                          ? 'bg-brand-pale font-medium text-brand-primary'
                          : 'text-ink-800 hover:bg-surface-sunken',
                      )
                    }
                  >
                    <item.icon className="h-4 w-4 shrink-0" strokeWidth={1.75} />
                    <span className="flex-1 truncate">{item.label}</span>
                    {item.stub && (
                      <span className="shrink-0 rounded-pill bg-cream-100 px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wider text-ink-800">
                        bientôt
                      </span>
                    )}
                  </NavLink>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </nav>
    </aside>
  );
}
